import { Component, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import DefaultFallback, { type IErrorFallbackProps } from './DefaultFallback';

export type { IErrorFallbackProps };

interface IProps {
    children?: ReactNode;
    fallback?: ComponentType<IErrorFallbackProps>;
}

interface IState {
    error: Error | null;
}

// Dependency-free React error boundary — no react-error-boundary in
// package.json, and this stays a small enough class that adding it isn't
// worth a new dependency. Catches a render crash ANYWHERE in `children` and
// replaces it with a fallback instead of letting the error propagate to the
// React root and blank the whole page (see 68df4b7a, the dev-portal Sales
// search crash that motivated this). Defaults to DefaultFallback (safe
// anywhere in the app); callers needing a section-specific look (e.g. the
// dev portal's black-gold DevErrorFallback) pass their own `fallback`.
class ErrorBoundary extends Component<IProps, IState> {
    state: IState = { error: null };

    static getDerivedStateFromError(error: Error): IState {
        return { error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // No app-wide logger/telemetry exists in this repo yet (grep confirms
        // — see agent-memory) — console.error is the only sink, but it still
        // carries the component stack so the crash is debuggable from
        // devtools or any future log shipper reading console output.
        console.error('[ErrorBoundary] Caught a render error', error, errorInfo.componentStack);
    }

    render(): ReactNode {
        const { error } = this.state;
        const { children, fallback: Fallback = DefaultFallback } = this.props;
        if (error) {
            return <Fallback error={error} />;
        }
        return children;
    }
}

export default ErrorBoundary;
